import { LoadingOverlay } from "@/components/LoadingOverlay";
import { Switch } from "@/components/ui/switch";
import { showApiError, showSuccess } from "@/lib/toast";
import {
  addUserSystemDynamicSetting,
  getSystemDynamicSettings,
  getUserSystemDynamicSettings,
  removeUserSystemDynamicSetting,
} from "@/sideEffects/api";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

interface Props {
  userId: string;
  selectedFirmId: string;
}

export const UserSystemDynamicSettingsRaw: React.FC<Props> = ({ userId, selectedFirmId }) => {
  const { t } = useTranslation();

  const queryClient = useQueryClient();

  const { data: systemDynamicSettings, isLoading: isSystemDynamicSettingsLoading } = useQuery({
    queryKey: ["getSystemDynamicSettings"],
    queryFn: () => getSystemDynamicSettings(),
  });

  const { data: userSystemDynamicSettings, isLoading: isUserSystemDynamicSettingsLoading } = useQuery({
    queryKey: ["getUserSystemDynamicSettings", userId],
    queryFn: () => getUserSystemDynamicSettings(userId, selectedFirmId),
  });

  const addUserSystemDynamicSettingMutation = useMutation({
    mutationFn: (systemDynamicSettingId: string) =>
      addUserSystemDynamicSetting(userId, systemDynamicSettingId, String(selectedFirmId)),
    onError: showApiError,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["getUserSystemDynamicSettings"],
      });
      showSuccess(t("common.addedSuccess"));
    },
  });

  const removeUserSystemDynamicSettingMutation = useMutation({
    mutationFn: (systemDynamicSettingId: string) =>
      removeUserSystemDynamicSetting(userId, systemDynamicSettingId, String(selectedFirmId)),
    onError: showApiError,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["getUserSystemDynamicSettings"],
      });
      showSuccess(t("common.deletedSuccess"));
    },
  });

  return (
    <LoadingOverlay
      isLoading={
        isSystemDynamicSettingsLoading ||
        isUserSystemDynamicSettingsLoading ||
        addUserSystemDynamicSettingMutation.isPending ||
        removeUserSystemDynamicSettingMutation.isPending
      }
    >
      {selectedFirmId ? (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          {(systemDynamicSettings?.data.result || []).map((systemDynamicSetting) => {
            const relation = (userSystemDynamicSettings?.data.result || []).find(
              (userSetting) => userSetting.systemUserDynamicSettingId === systemDynamicSetting.id
            );

            const isSelected = !!relation;

            return (
              <div key={systemDynamicSetting.id} className="flex items-center justify-between p-2 border-b">
                <div className="font-medium">{systemDynamicSetting.name}</div>
                <Switch
                  checked={isSelected}
                  onCheckedChange={(newChecked) => {
                    if (newChecked) {
                      addUserSystemDynamicSettingMutation.mutate(systemDynamicSetting.id);
                    } else if (relation) {
                      removeUserSystemDynamicSettingMutation.mutate(relation.id);
                    }
                  }}
                />
              </div>
            );
          })}
        </div>
      ) : null}
    </LoadingOverlay>
  );
};
